// src/personel-takip/src/pages/KaraListe.js
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  collection, getDocs, query, where, addDoc, doc, deleteDoc, serverTimestamp
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import Select from "react-select";

/* ---------------- Helpers ---------------- */
const U = (s = "") => String(s || "").toLocaleUpperCase("tr-TR");
const L = (s = "") => String(s || "").toLocaleLowerCase("tr-TR");
const byLocale = (a, b) => (a || "").localeCompare(b || "", "tr", { sensitivity: "base" });
const nameCaseTR = (s = "") =>
  L(s).split(/\s+/).filter(Boolean).map(w => w[0]?.toLocaleUpperCase("tr-TR") + w.slice(1)).join(" ");

/* ---- aktif/pasif hesap (girisX/cikisX) ---- */
const parseDotOrISO = (v) => {
  if (!v) return null;
  if (v.toDate) return v.toDate();
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s);
  const t = s.replaceAll("/", ".").replaceAll("-", ".");
  const m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (!m) return null;
  return new Date(+m[3], +m[2] - 1, +m[1]);
};
const getIntervals = (tarih = {}) => {
  const g = [], c = [];
  for (const [k, v] of Object.entries(tarih)) {
    if (!v) continue;
    if (k.startsWith("giris")) g.push([k, parseDotOrISO(v)]);
    if (k.startsWith("cikis")) c.push([k, parseDotOrISO(v)]);
  }
  const num = (s) => +(s.match(/\d+/)?.[0] || 0);
  g.sort((a, b) => num(a[0]) - num(b[0]));
  c.sort((a, b) => num(a[0]) - num(b[0]));
  const out = [];
  for (let i = 0; i < g.length; i++) {
    const s = g[i][1];
    const e = c[i]?.[1] || null;
    if (s) out.push([s, e]);
  }
  return out;
};
const isActiveToday = (tarih = {}) => {
  const ivs = getIntervals(tarih);
  const dt = new Date(); dt.setHours(0,0,0,0);
  return ivs.some(([s, e]) => {
    const end = e ? new Date(e.getFullYear(), e.getMonth(), e.getDate() + 1) : null;
    return (s <= dt) && (!end || end > dt);
  });
};

/* ---- yetki: oy verebilir mi? ---- */
async function userCanVoteBlacklist(email) {
  if (!email) return false;
  const snap = await getDocs(
    query(collection(db, "users"), where("email", "==", email), where("canBlacklist", "==", true))
  );
  return !snap.empty;
}

/* ---- oy haritası (blacklistVotes üst koleksiyon) ----
   personelId -> { count, voters: Set<email>, reasons: string[] }
*/
async function fetchVotesMapFromTop() {
  const snap = await getDocs(collection(db, "blacklistVotes"));
  const map = new Map();
  snap.forEach((d) => {
    const v = d.data() || {};
    const pid = v.personelId;
    const by  = String(v.createdBy || "").toLowerCase();
    if (!pid || !by) return;
    if (!map.has(pid)) map.set(pid, { count: 0, voters: new Set(), reasons: [] });
    const rec = map.get(pid);
    if (!rec.voters.has(by)) {
      rec.voters.add(by);
      rec.count += 1;
    }
    if (typeof v.reason === "string" && v.reason.trim() && rec.reasons.length < 3) {
      rec.reasons.push(v.reason.trim());
    }
  });
  return map;
}

/* ================== Component ================== */
const KaraListe = () => {
  const me = auth?.currentUser?.email?.toLowerCase() || "";

  // yetki/görüş
  const [allowedTesis, setAllowedTesis] = useState([]);
  const [canVote, setCanVote] = useState(false);

  // veriler
  const [allPersons, setAllPersons] = useState([]);
  const [loading, setLoading] = useState(true);

  // oy verileri
  const [votesMap, setVotesMap] = useState(new Map()); // personelId -> { count, voters:Set, reasons:string[] }
  const voteCountFor = (pid) => votesMap.get(pid)?.count || 0;
  const meVotedFor   = (pid) => votesMap.get(pid)?.voters?.has(me) || false;

  // filtreler
  const [textSearch, setTextSearch] = useState("");
  const [selSites, setSelSites] = useState([]);
  const [showPassive, setShowPassive] = useState(false);
  const [voteFilter, setVoteFilter] = useState("ANY"); // ANY, EQ1, EQ2, EQ3, GE1, GE2, GE3

  // oy modal (opsiyonel gerekçe)
  const [votingPid, setVotingPid] = useState(null);
  const [reasonDraft, setReasonDraft] = useState("");
  const modalRef = useRef(null);

  /* ---- yetkili tesisleri bul ---- */
  useEffect(() => {
    (async () => {
      try {
        if (!me) { setAllowedTesis([]); setCanVote(false); return; }

        // personeller'de kendi kaydından tesis + gorebilecegiTesisler
        const qP = query(collection(db, "personeller"), where("email", "==", me));
        const sP = await getDocs(qP);
        const allow = new Set();
        sP.forEach((doc) => {
          const data = doc.data() || {};
          if (data.tesis) allow.add(U(data.tesis));
          let extra = data.gorebilecegiTesisler;
          if (typeof extra === "string") extra = extra.split(",").map(x => x.trim()).filter(Boolean);
          (extra || []).forEach(t => allow.add(U(t)));
        });
        setAllowedTesis(Array.from(allow));

        // kara liste oy yetkisi
        setCanVote(await userCanVoteBlacklist(me));
      } catch (e) {
        console.warn("izin okuma hatası", e);
        setAllowedTesis([]); setCanVote(false);
      }
    })();
  }, [me]);

  /* ---- personeller ---- */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, "personeller"));
        let arr = snap.docs.map(d => {
          const v = d.data() || {};
          return { id: d.id, ...v, _activeToday: isActiveToday(v.tarih || {}) };
        });
        const allow = new Set(allowedTesis);
        arr = arr.filter(p => allow.has(U(p.tesis || "")));
        arr.sort((a, b) => byLocale(a.ad, b.ad) || byLocale(a.soyad, b.soyad) || byLocale(a.tesis, b.tesis));
        setAllPersons(arr);
      } catch (e) {
        console.error("personeller okunamadı", e);
        setAllPersons([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [allowedTesis]);

  /* ---- oy haritasını çek ---- */
  const reloadVotes = async () => setVotesMap(await fetchVotesMapFromTop());
  useEffect(() => { reloadVotes(); }, []);

  /* ---- tesis seçenekleri ---- */
  const siteOptions = useMemo(() => {
    const s = new Set();
    allPersons.forEach(p => p.tesis && s.add(U(p.tesis)));
    return Array.from(s).sort(byLocale).map(v => ({ value: v, label: v }));
  }, [allPersons]);

  /* ---- filtreli temel liste ---- */
  const baseFiltered = useMemo(() => {
    const siteSet = new Set(selSites);
    return allPersons.filter(p => {
      if (!showPassive && !p._activeToday) return false;
      if (siteSet.size && !siteSet.has(U(p.tesis || ""))) return false;
      if (textSearch) {
        const s = L(textSearch);
        const hay = `${p.ad} ${p.soyad} ${p.tesis} ${p.gorev}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [allPersons, selSites, textSearch, showPassive]);

  /* ---- oy filtresi uygula ---- */
  const filteredPersons = useMemo(() => {
    if (voteFilter === "ANY") return baseFiltered;
    const ok = (pid) => {
      const t = voteCountFor(pid);
      switch (voteFilter) {
        case "EQ1": return t === 1;
        case "EQ2": return t === 2;
        case "EQ3": return t === 3;
        case "GE1": return t >= 1;
        case "GE2": return t >= 2;
        case "GE3": return t >= 3;
        default: return true;
      }
    };
    return baseFiltered.filter(p => ok(p.id));
  }, [baseFiltered, voteFilter, votesMap]);

  /* ---- oy ver / geri al (index gerektirmeden) ---- */
  const openVote = (pid) => { setVotingPid(pid); setReasonDraft(""); };
  const closeVote = () => { setVotingPid(null); setReasonDraft(""); };

  const submitVote = async () => {
    if (!votingPid || !canVote) return;
    const pid = votingPid;
    if (meVotedFor(pid)) { alert("Bu personele zaten oy verdiniz."); closeVote(); return; }
    if (voteCountFor(pid) >= 3) { alert("Zaten 3/3 kara listede."); closeVote(); return; }
    try {
      await addDoc(collection(db, "blacklistVotes"), {
        personelId: pid,
        createdBy: me,
        createdAt: serverTimestamp(),
        ...(reasonDraft.trim() ? { reason: reasonDraft.trim() } : {}) // opsiyonel
      });
      await reloadVotes();
      alert(`Oyunuz kaydedildi. Toplam oy: ${voteCountFor(pid) + 1}/3`);
    } catch (e) {
      console.error("oy verilemedi", e);
      alert("Oy verilemedi.");
    } finally {
      closeVote();
    }
  };

  // composite index ihtiyacını aşmak için: createdBy==me ile daralt, personelId'yi client'ta eşle
  const undoVote = async (pid) => {
    if (voteCountFor(pid) >= 3) return; // 3 ve üzeri ise geri alınmasın
    try {
      const mineSnap = await getDocs(query(collection(db, "blacklistVotes"), where("createdBy", "==", me)));
      let targetId = null;
      mineSnap.forEach((d) => {
        const v = d.data() || {};
        if (!targetId && v.personelId === pid) targetId = d.id;
      });
      if (!targetId) { alert("Bu personele verdiğiniz bir oy bulunamadı."); return; }
      await deleteDoc(doc(db, "blacklistVotes", targetId));
      await reloadVotes();
      alert("Oyunuz geri alındı.");
    } catch (e) {
      console.error("oy geri alınamadı", e);
      alert("Oy geri alınamadı.");
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div style={{ padding: 14, fontFamily: 'system-ui,-apple-system,"Segoe UI",Roboto,Arial' }}>
      <style>{`
        .layout{ display:grid; grid-template-columns: 360px 1fr; gap:12px; }
        @media (max-width: 980px){ .layout{ grid-template-columns: 1fr; } }
        .panel{ border:1px solid #e5e7eb; border-radius:10px; background:#fff; }
        .head{ padding:10px 12px; border-bottom:1px solid #e5e7eb; font-weight:700; display:flex; align-items:center; justify-content:space-between; gap:8px; }
        .body{ padding:10px 12px; }
        .row{ display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
        input[type="text"], select{
          padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; box-sizing:border-box;
        }
        .btn{ padding:8px 12px; border-radius:8px; border:1px solid #d1d5db; background:#111827; color:#fff; font-weight:600; cursor:pointer; }
        .btn.light{ background:#fff; color:#111827; }
        .btn.red{ background:#991b1b; border-color:#991b1b; color:#fff; }
        .muted{ color:#6b7280; font-size:12px; }
        .person{ display:flex; align-items:flex-start; justify-content:space-between; gap:8px; padding:10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:8px; }
        .person:hover{ background:#f8fafc; }
        .chip{ padding:2px 8px; border:1px solid #e5e7eb; background:#f1f5f9; border-radius:999px; font-size:12px; }
        .badge{ display:inline-flex; align-items:center; gap:6px; padding:2px 8px; border-radius:999px; background:#f1f5f9; border:1px solid #e5e7eb; font-size:12px; }
        .list{ max-height: calc(100vh - 300px); overflow:auto; }
        .pad{ padding:6px 0; }
        .toggle{ display:inline-flex; align-items:center; gap:6px; font-size:12px; cursor:pointer; user-select:none; }
        .toggle input{ transform: translateY(1px); }
        .sel{ padding:8px 10px; border:1px solid #d1d5db; border-radius:8px; font-size:13px; }
        .reasons{ margin-top:6px; font-size:12px; color:#334155; }
        .one-reason{ display:inline-block; background:#f8fafc; border:1px solid #e5e7eb; border-radius:6px; padding:3px 6px; margin-right:6px; margin-top:4px; }
      `}</style>

      <h2>Kara Liste</h2>

      <div className="layout">
        {/* SOL: Filtreler */}
        <div className="panel">
          <div className="head">Filtre</div>
          <div className="body">
            <div className="row" style={{ marginBottom: 8 }}>
              <input
                type="text"
                placeholder="Ara (ad / soyad / görev / tesis)"
                value={textSearch}
                onChange={(e) => setTextSearch(e.target.value)}
                style={{ flex: "1 1 180px" }}
              />
            </div>

            <div className="row" style={{ alignItems: "stretch" }}>
              <div style={{ flex: "1 1 100%", minWidth: 260 }}>
                <div className="muted">Tesis (çoklu)</div>
                <Select
                  isMulti
                  options={siteOptions}
                  value={siteOptions.filter((o) => selSites.includes(o.value))}
                  onChange={(vals) => setSelSites((vals || []).map((v) => v.value))}
                  placeholder="Tesis seç..."
                />
              </div>
            </div>

            <div className="pad">
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={showPassive}
                  onChange={(e)=>setShowPassive(e.target.checked)}
                />
                Çıkanları da göster
              </label>
            </div>

            <div className="pad">
              <div className="muted" style={{ marginBottom: 4 }}>Oy filtresi</div>
              <select className="sel" value={voteFilter} onChange={(e)=>setVoteFilter(e.target.value)}>
                <option value="ANY">Hepsi</option>
                <option value="EQ1">Tam 1</option>
                <option value="EQ2">Tam 2</option>
                <option value="EQ3">Tam 3</option>
                <option value="GE1">1 ve üzeri</option>
                <option value="GE2">2 ve üzeri</option>
                <option value="GE3">3 ve üzeri</option>
              </select>
            </div>

            <div className="muted" style={{ marginTop: 8 }}>
              Görünen kişiler sadece <b>yetkin olan</b> tesislerdendir.
            </div>
          </div>
        </div>

        {/* SAĞ: Liste */}
        <div className="panel">
          <div className="head">
            <div>Personeller</div>
            <div className="muted">{loading ? "Yükleniyor…" : `${filteredPersons.length} kişi`}</div>
          </div>
          <div className="body">
            {loading && <div className="muted">Veriler yükleniyor…</div>}
            {!loading && !filteredPersons.length && <div className="muted">(Kayıt yok)</div>}

            <div className="list">
              {filteredPersons.map((p) => {
                const count = voteCountFor(p.id);
                const reached3 = count >= 3;
                const iVoted = meVotedFor(p.id);
                const reasons = votesMap.get(p.id)?.reasons || [];

                return (
                  <div className="person" key={p.id}>
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>
                          {nameCaseTR(p.ad || "")} {U(p.soyad || "")}
                        </div>
                        {!p._activeToday && <span className="badge" title="Bugün pasif">Pasif</span>}
                        {reached3 && <span className="badge" title="3 oy eşiği aşıldı">3+ Oy</span>}
                      </div>
                      <div className="muted">
                        {U(p.gorev || "-")} — {U(p.tesis || "-")}
                      </div>

                      <div style={{ marginTop: 6 }}>
                        <span className="chip">Kara Liste Oy: <b>{count}</b></span>
                        {iVoted && <span className="chip" style={{ marginLeft: 6 }}>Senin oyun var</span>}
                      </div>

                      {reasons.length > 0 && (
                        <div className="reasons">
                          <b>Gerekçe örnekleri:</b>{" "}
                          {reasons.map((r, i) => <span key={i} className="one-reason">{r}</span>)}
                        </div>
                      )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end", minWidth: 240 }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {!iVoted ? (
                          <button
                            className="btn"
                            onClick={() => openVote(p.id)}
                            disabled={!canVote || reached3}
                            title={reached3 ? "3 oy eşiği aşıldı" : (canVote ? "Oy Ver" : "Yetkiniz yok")}
                          >
                            Oy Ver
                          </button>
                        ) : (
                          <button
                            className="btn red"
                            onClick={() => undoVote(p.id)}
                            disabled={reached3}
                            title={reached3 ? "Toplam oy 3’e ulaştı: Geri alma kapalı" : "Oyu Geri Al"}
                          >
                            Oyu Geri Al
                          </button>
                        )}
                        <button className="btn light" onClick={reloadVotes}>Yenile</button>
                      </div>

                      <div className="muted" style={{ maxWidth: 260, textAlign: "right" }}>
                        {reached3
                          ? "Bu personel 3 oy eşiğini geçti. Oy verme/geri alma kapalı."
                          : "Her kullanıcı kişi başına 1 oy verir. (İsteğe bağlı gerekçe kaydedilebilir.)"}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="muted" style={{ marginTop: 8 }}>
              İpucu: Çok kişi varsa önce filtrele; “Yenile” ile oy sayıları güncellenir.
            </div>
          </div>
        </div>
      </div>

      {/* Oy Modal (opsiyonel gerekçe alanı) */}
      {votingPid && (
        <div
          style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:100 }}
          onClick={closeVote}
        >
          <div
            ref={modalRef}
            onClick={(e)=>e.stopPropagation()}
            style={{ width:"min(560px, 94vw)", background:"#fff", borderRadius:10, overflow:"hidden" }}
          >
            <div style={{ padding:"10px 12px", borderBottom:"1px solid #e5e7eb", fontWeight:700 }}>Kara Liste Oyu</div>
            <div style={{ padding:12 }}>
              <div style={{ border:"1px dashed #cbd5e1", borderRadius:8, padding:8, background:"#f8fafc" }}>
                <div className="muted" style={{ marginBottom: 6 }}>Gerekçe (opsiyonel)</div>
                <textarea
                  rows={4}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 8, fontSize: 13 }}
                  placeholder="Kısa bir not bırakabilirsiniz… (opsiyonel)"
                  value={reasonDraft}
                  onChange={(e) => setReasonDraft(e.target.value)}
                />
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                  <button className="btn light" onClick={closeVote}>Vazgeç</button>
                  <button className="btn" onClick={submitVote}>Kaydet</button>
                </div>
              </div>
              <div className="muted" style={{ marginTop: 8 }}>
                Not: Personel Listesi’nde olduğu gibi oylar <code>blacklistVotes</code> üst koleksiyonunda tutulur.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KaraListe;
